import { buildItemsFromSelectedServices, buildUserDataObject } from "./gtmEcommerce";
import { buildGtmEventFields } from "./gtmEventFields";

const normalizeDateToken = (value) => value
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "_")
  .replace(/^_+|_+$/g, "");

export const formatReservationDate = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;

  return normalizeDateToken(new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date));
};

export const formatReservationTime = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${String(value.getHours()).padStart(2, "0")}h${String(value.getMinutes()).padStart(2, "0")}`;
  }

  const match = String(value ?? "").trim().match(/^(\d{1,2})[:h](\d{2})$/i);
  if (!match) return undefined;

  return `${String(Number(match[1])).padStart(2, "0")}h${match[2]}`;
};

const formatFlyproName = (flypro) => {
  // No flypro data at all (e.g. choix_date/choix_heure, before that step)
  // is distinct from an explicit "no preference" selection (id === 0).
  if (flypro === null || flypro === undefined) return undefined;
  if (flypro.id === 0) return "no_preference";

  const firstName = String(flypro.firstname ?? flypro.first_name ?? "").trim();
  const lastName = String(flypro.lastname ?? flypro.last_name ?? "").trim();
  if (firstName && lastName) return `${firstName}_${lastName.charAt(0).toUpperCase()}`;

  const fullName = String(flypro?.extra?.full_name ?? flypro.full_name ?? flypro.name ?? "").trim();
  if (!fullName) return undefined;
  const [first, last] = fullName.split(/\s+/);
  return last ? `${first}_${last.charAt(0).toUpperCase()}` : first;
};

export const formatReservationFlyproName = (flypros) => {
  const values = (Array.isArray(flypros) ? flypros : [flypros])
    .map(formatFlyproName)
    .filter(Boolean);

  if (!values.length) return undefined;
  if (values.includes("no_preference")) return "no_preference";
  return [...new Set(values)].join(",");
};

export const buildReservationTrackingPayload = ({
  date,
  time,
  flypros,
  selectedServices,
  address,
  phone,
  language,
  user,
} = {}) => {
  const isConnectedUser = Boolean(user?.id);
  const fields = buildGtmEventFields({ language, user });
  const payload = {
    date: formatReservationDate(date),
    heure: formatReservationTime(time),
    flypro_name: formatReservationFlyproName(flypros),
    items: buildItemsFromSelectedServices(selectedServices, { address }),
    language: fields.language,
  };

  if (isConnectedUser) {
    payload.user_data = buildUserDataObject({
      user,
      address,
      phoneNumber: phone || user?.phone,
      emailAddress: user?.email,
      isConnectedUser,
    });
  }

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined)
  );
};
